import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, File as FileIcon, Folder, FolderPlus, Link2, RotateCcw, Trash2, Upload } from 'lucide-react';
import {
  type DriveFile,
  type DriveFileVersion,
  type DriveFolder,
  type PublicLink,
  type TrashContents,
  createFolder,
  listFiles,
  listFolders,
  listTrash,
  listVersions,
  mintFilePublicLink,
  mintFolderPublicLink,
  restoreFile,
  restoreFolder,
  trashFile,
  trashFolder,
  uploadFile,
  uploadVersion,
} from '@/api/drive';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface Crumb {
  id: string | null;
  name: string;
}

function fmtSize(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function DrivePage() {
  const { t } = useTranslation();
  const [crumbs, setCrumbs] = useState<Crumb[]>([{ id: null, name: 'Drive' }]);
  const [folders, setFolders] = useState<DriveFolder[]>([]);
  const [files, setFiles] = useState<DriveFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'browse' | 'trash'>('browse');
  const [trash, setTrash] = useState<TrashContents>({ folders: [], files: [] });
  const [newFolder, setNewFolder] = useState('');
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [versionsFor, setVersionsFor] = useState<DriveFile | null>(null);
  const newFileInputRef = useRef<HTMLInputElement>(null);
  const versionInputRef = useRef<HTMLInputElement>(null);
  const versionTargetRef = useRef<string | null>(null);

  const currentId = crumbs[crumbs.length - 1].id ?? undefined;

  const load = useCallback(() => {
    setLoading(true);
    if (view === 'trash') {
      listTrash()
        .then(setTrash)
        .catch(() => setTrash({ folders: [], files: [] }))
        .finally(() => setLoading(false));
      return;
    }
    Promise.all([listFolders(currentId).catch(() => []), listFiles(currentId).catch(() => [])])
      .then(([fo, fi]) => {
        setFolders(fo);
        setFiles(fi);
      })
      .finally(() => setLoading(false));
  }, [view, currentId]);

  useEffect(() => {
    load();
  }, [load]);

  function openFolder(f: DriveFolder) {
    setCrumbs((c) => [...c, { id: f.id, name: f.name }]);
  }
  function goTo(i: number) {
    setCrumbs((c) => c.slice(0, i + 1));
  }

  async function addFolder() {
    if (!newFolder.trim()) return;
    setCreating(true);
    try {
      await createFolder(newFolder.trim(), currentId);
      setNewFolder('');
      load();
    } finally {
      setCreating(false);
    }
  }

  async function onUploadNew(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      await uploadFile(file, currentId);
      load();
    } finally {
      setUploading(false);
    }
  }

  function pickVersion(fileId: string) {
    versionTargetRef.current = fileId;
    versionInputRef.current?.click();
  }

  async function onUploadVersion(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    const fileId = versionTargetRef.current;
    versionTargetRef.current = null;
    if (!file || !fileId) return;
    setUploading(true);
    try {
      await uploadVersion(fileId, file);
      load();
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-lop-text-primary)]">{t('drive.title')}</h1>
          <p className="text-sm text-[var(--color-lop-text-secondary)]">{t('drive.subtitle')}</p>
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant={view === 'browse' ? 'default' : 'ghost'} onClick={() => setView('browse')}>
            {t('drive.browse')}
          </Button>
          <Button size="sm" variant={view === 'trash' ? 'default' : 'ghost'} onClick={() => setView('trash')} data-testid="drive-trash-tab">
            <Trash2 className="mr-1 h-4 w-4" />
            {t('drive.trash')}
          </Button>
        </div>
      </header>

      {view === 'browse' && (
        <>
          <div className="flex flex-wrap items-center gap-1 text-sm text-[var(--color-lop-text-secondary)]">
            {crumbs.map((c, i) => (
              <span key={c.id ?? 'root'} className="flex items-center gap-1">
                {i > 0 && <span className="text-[var(--color-lop-text-muted)]">/</span>}
                <button
                  type="button"
                  className="hover:text-[var(--color-lop-text-primary)] hover:underline"
                  onClick={() => goTo(i)}
                >
                  {i === 0 ? t('drive.title') : c.name}
                </button>
              </span>
            ))}
          </div>

          <div className="flex items-end gap-2">
            <Input
              value={newFolder}
              onChange={(e) => setNewFolder(e.target.value)}
              placeholder={t('drive.newFolderName')}
              className="max-w-xs"
              data-testid="drive-new-folder-name"
            />
            <Button size="sm" variant="outline" onClick={addFolder} disabled={creating || !newFolder.trim()} data-testid="drive-new-folder">
              <FolderPlus className="mr-1 h-4 w-4" />
              {t('drive.newFolder')}
            </Button>
            <Button
              size="sm"
              onClick={() => newFileInputRef.current?.click()}
              disabled={uploading}
              data-testid="drive-upload"
            >
              <Upload className="mr-1 h-4 w-4" />
              {uploading ? t('drive.uploading') : t('drive.upload')}
            </Button>
            <input ref={newFileInputRef} type="file" className="hidden" onChange={onUploadNew} data-testid="drive-upload-input" />
            <input ref={versionInputRef} type="file" className="hidden" onChange={onUploadVersion} data-testid="drive-version-input" />
          </div>
        </>
      )}

      {loading && <p className="p-6 text-center text-sm text-muted-foreground">{t('common.loading')}</p>}

      {!loading && view === 'browse' && (
        <div className="overflow-hidden rounded-lg border bg-card">
          {folders.length === 0 && files.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">{t('drive.empty')}</p>
          ) : (
            <ul className="divide-y divide-[var(--color-lop-border)]">
              {folders.map((f) => (
                <li key={f.id} className="flex items-center gap-3 px-4 py-2.5" data-testid="drive-folder">
                  <button type="button" className="flex min-w-0 flex-1 items-center gap-3 text-left" onClick={() => openFolder(f)}>
                    <Folder className="h-5 w-5 shrink-0 text-[var(--color-lop-teal)]" />
                    <span className="truncate text-sm text-[var(--color-lop-text-primary)]">{f.name}</span>
                  </button>
                  <ShareAction mint={() => mintFolderPublicLink(f.id)} title={t('drive.copyLink')} />
                  <RowAction onTrash={async () => { await trashFolder(f.id); load(); }} title={t('drive.moveToTrash')} />
                </li>
              ))}
              {files.map((f) => (
                <li key={f.id} className="flex items-center gap-3 px-4 py-2.5" data-testid="drive-file">
                  <FileIcon className="h-5 w-5 shrink-0 text-[var(--color-lop-text-muted)]" />
                  <span className="min-w-0 flex-1 truncate text-sm text-[var(--color-lop-text-primary)]">{f.name}</span>
                  <span className="text-xs text-[var(--color-lop-text-muted)]">{fmtSize(f.sizeBytes)}</span>
                  <button type="button" className="text-xs text-[var(--color-lop-teal)] hover:underline" onClick={() => setVersionsFor(f)}>
                    {t('drive.versions')}
                  </button>
                  <button
                    type="button"
                    className="text-[var(--color-lop-text-muted)] hover:text-[var(--color-lop-teal)]"
                    onClick={() => pickVersion(f.id)}
                    disabled={uploading}
                    title={t('drive.uploadVersion')}
                    data-testid="drive-upload-version"
                  >
                    <Upload className="h-4 w-4" />
                  </button>
                  <ShareAction mint={() => mintFilePublicLink(f.id)} title={t('drive.copyLink')} />
                  <RowAction onTrash={async () => { await trashFile(f.id); load(); }} title={t('drive.moveToTrash')} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!loading && view === 'trash' && (
        <div className="overflow-hidden rounded-lg border bg-card">
          {trash.folders.length === 0 && trash.files.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">{t('drive.trashEmpty')}</p>
          ) : (
            <ul className="divide-y divide-[var(--color-lop-border)]">
              {trash.folders.map((f) => (
                <li key={f.id} className="flex items-center gap-3 px-4 py-2.5">
                  <Folder className="h-5 w-5 shrink-0 text-[var(--color-lop-text-muted)]" />
                  <span className="min-w-0 flex-1 truncate text-sm text-[var(--color-lop-text-secondary)]">{f.name}</span>
                  <Button size="sm" variant="ghost" onClick={async () => { await restoreFolder(f.id); load(); }}>
                    <RotateCcw className="mr-1 h-4 w-4" />
                    {t('drive.restore')}
                  </Button>
                </li>
              ))}
              {trash.files.map((f) => (
                <li key={f.id} className="flex items-center gap-3 px-4 py-2.5">
                  <FileIcon className="h-5 w-5 shrink-0 text-[var(--color-lop-text-muted)]" />
                  <span className="min-w-0 flex-1 truncate text-sm text-[var(--color-lop-text-secondary)]">{f.name}</span>
                  <Button size="sm" variant="ghost" onClick={async () => { await restoreFile(f.id); load(); }}>
                    <RotateCcw className="mr-1 h-4 w-4" />
                    {t('drive.restore')}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {versionsFor && <VersionsModal file={versionsFor} onClose={() => setVersionsFor(null)} />}
    </div>
  );
}

function ShareAction({ mint, title }: { mint: () => Promise<PublicLink>; title: string }) {
  const [copied, setCopied] = useState(false);
  async function share() {
    try {
      const { url } = await mint();
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard or mint failed — non-fatal */
    }
  }
  return (
    <button
      type="button"
      onClick={share}
      title={title}
      className="text-[var(--color-lop-text-muted)] hover:text-[var(--color-lop-teal)]"
      data-testid="drive-share-action"
    >
      {copied ? (
        <Check className="h-4 w-4 text-[var(--color-lop-teal)]" />
      ) : (
        <Link2 className="h-4 w-4" />
      )}
    </button>
  );
}

function RowAction({ onTrash, title }: { onTrash: () => void | Promise<void>; title: string }) {
  return (
    <button
      type="button"
      onClick={onTrash}
      title={title}
      className="text-[var(--color-lop-text-muted)] hover:text-red-500"
      data-testid="drive-trash-action"
    >
      <Trash2 className="h-4 w-4" />
    </button>
  );
}

function VersionsModal({ file, onClose }: { file: DriveFile; onClose: () => void }) {
  const { t } = useTranslation();
  const [versions, setVersions] = useState<DriveFileVersion[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listVersions(file.id)
      .then(setVersions)
      .catch(() => setVersions([]))
      .finally(() => setLoading(false));
  }, [file.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[80vh] w-full max-w-md overflow-y-auto rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-1 text-lg font-semibold">{t('drive.versionsModal.title')}</h3>
        <p className="mb-3 truncate text-sm text-[var(--color-lop-text-secondary)]">{file.name}</p>
        {loading ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : versions.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('drive.versionsModal.empty')}</p>
        ) : (
          <ul className="divide-y divide-[var(--color-lop-border)]">
            {versions.map((v) => (
              <li key={v.id} className="flex items-center justify-between py-2 text-sm">
                <span className="text-[var(--color-lop-text-primary)]">v{v.version}</span>
                <span className="text-xs text-[var(--color-lop-text-muted)]">
                  {fmtSize(v.sizeBytes)} · {v.uploadedAt.slice(0, 10)}
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-4 flex justify-end">
          <Button size="sm" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      </div>
    </div>
  );
}
